import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn, TTheme } from '@feathr/components';
import { ContextMenu, Pill, TableColumnHeader, Text } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISamplePartner } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';
import buildPillColumn from './buildPillColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const ROLE_THEME: Record<ISamplePartner['role'], TTheme> = {
  exhibitor: 'sky',
  sponsor: 'violet',
  speaker: 'cyan',
};

const ROLE_LABEL: Record<ISamplePartner['role'], string> = {
  exhibitor: 'Exhibitor',
  sponsor: 'Sponsor',
  speaker: 'Speaker',
};

interface IPartnersColumnsArgs {
  base: string;
  t: TFunction;
}

function partnersColumns({ base, t }: IPartnersColumnsArgs): Array<IColumn<ISamplePartner>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Partner'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 240,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
    },
    buildPillColumn<ISamplePartner, ISamplePartner['role']>({
      accessor: 'role',
      labelMap: ROLE_LABEL,
      t,
      themeMap: ROLE_THEME,
      title: t('Role'),
    }),
    {
      accessor: 'contact',
      id: 'contact',
      Header: TableColumnHeader({ title: t('Contact'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      Cell({ original }): JSX.Element {
        return <Text variant={'smallRegular'}>{original.contact}</Text>;
      },
    },
    {
      accessor: (row): number => (row.optedIn ? 1 : 0),
      id: 'optedIn',
      Header: TableColumnHeader({ title: t('Opted in'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        return (
          <Pill theme={original.optedIn ? 'green' : 'neutral'}>
            {original.optedIn ? t('Yes') : t('No')}
          </Pill>
        );
      },
    },
    buildOptionsColumn<ISamplePartner>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('edit')}>
            {t('Edit')}
          </ContextMenu.Item>
          <DemoMenuItem action={'resend'} label={t('Resend invite')} />
          <DemoMenuItem action={'delete'} isDestructive={true} label={t('Remove')} />
        </>
      ),
    }),
  ];
}

export default partnersColumns;
